/**
 * Small pieces of kit markup that carry attributes which change between
 * renders.
 *
 * `@hydrogen/ui` components apply their `attributes` once, when an instance
 * mounts, so a link whose address changes cannot be one. The kit's markup is
 * its API (`data-slot`, `data-variant`, `data-size`, `ui-*` classes), so these
 * write that markup as a template and the binding stays live.
 */

import type { TemplateInstance } from 'hydrogen/runtime';
import { html, repeat } from 'hydrogen/runtime';

export type ButtonVariant = 'default' | 'secondary' | 'outline' | 'ghost' | 'link' | 'destructive';
export type ButtonSize = 'default' | 'xs' | 'sm' | 'lg' | 'icon' | 'icon-sm';

/** A 16-unit stroke mark. `path` is an SVG path from `nav.ts#navGlyph` or `Glyph`. */
export function Icon(path: string): TemplateInstance {
  return html`<svg class="mx-icon" viewBox="0 0 16 16" aria-hidden="true"><path d="${path}"></path></svg>`;
}

/** Marks drawn for the chrome and the states, beside the navigation set. */
export const GLYPH = {
  panel: 'M2.5 3h11v10h-11zM6 3v10',
  chevronsUpDown: 'M5 6.5 8 3.5l3 3M5 9.5l3 3 3-3',
  chevronRight: 'M6 3.5 10.5 8 6 12.5',
  back: 'M10 3.5 5.5 8l4.5 4.5',
  import: 'M2 11h8M8 8l3 3-3 3M14 5H6M8 2 5 5l3 3',
  alert: 'M8 2.5 14 13H2zM8 6.5v3M8 11.5h.01',
  logout: 'M6 2.5H3.5v11H6M9.5 4.5 12.5 8l-3 3.5M12.5 8H6',
  user: 'M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6M2.5 14c.6-2.7 2.7-4 5.5-4s4.9 1.3 5.5 4',
  database: 'M3 4.5C3 3.4 5.2 2.5 8 2.5s5 .9 5 2-2.2 2-5 2-5-.9-5-2zM3 4.5v7c0 1.1 2.2 2 5 2s5-.9 5-2v-7M3 8c0 1.1 2.2 2 5 2s5-.9 5-2',
  folder: 'M2 4.5h4l1.5 1.5H14v6.5H2zM2 4.5v-1h4',
  file: 'M4 2h5l3 3v9H4zM9 2v3h3',
  arrowRight: 'M3 8h10M9 4l4 4-4 4',
  arrowDown: 'M8 3v10M4 9l4 4 4-4',
  sun: 'M8 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1 1M11.6 11.6l1 1M3.4 12.6l1-1M11.6 4.4l1-1',
  moon: 'M13.5 9.5A5.5 5.5 0 1 1 6.5 2.5a4.5 4.5 0 0 0 7 7',
  monitor: 'M2 3h12v8H2zM6 14h4M8 11v3',
  home: 'M2.5 7.5 8 3l5.5 4.5M4 6.5V13h3V9.5h2V13h3V6.5',
} as const;

/**
 * A kit button that is a link. `spa` routes it inside the app; off, it is a full
 * page load. `icon` leads the label and `iconEnd` follows it.
 */
export function LinkButton(options: {
  href: string;
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  iconEnd?: string;
  spa?: boolean;
}): TemplateInstance {
  const { href, label } = options;
  const variant = options.variant ?? 'default';
  const size = options.size ?? 'sm';
  const icon = options.icon === undefined ? html`` : Icon(options.icon);
  const end = options.iconEnd === undefined ? html`` : Icon(options.iconEnd);
  if (options.spa === false) {
    return html`<a class="ui-button" data-slot="button" data-variant="${variant}" data-size="${size}" href="${href}">${icon}${label}${end}</a>`;
  }
  return html`<a class="ui-button" data-slot="button" data-variant="${variant}" data-size="${size}" href="${href}" data-nav="${href}">${icon}${label}${end}</a>`;
}

/** A kit button the app's click handler answers by `data-action`. */
export function ActionButton(options: {
  action: string;
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
}): TemplateInstance {
  const icon = options.icon === undefined ? html`` : Icon(options.icon);
  return html`<button type="button" class="ui-button" data-slot="button" data-variant="${options.variant ?? 'outline'}" data-size="${options.size ?? 'sm'}" data-action="${options.action}">${icon}${options.label}</button>`;
}

/**
 * The kit's NativeSelect around a `<select class="ui-native-select" data-slot="native-select">`
 * the caller writes, because the select carries the caller's own `data-*` hook
 * and its options bind live state.
 */
export function NativeSelectFrame(select: TemplateInstance): TemplateInstance {
  return html`<div class="ui-native-select-wrapper" data-slot="native-select-wrapper">${select}<svg class="ui-native-select-icon" data-slot="native-select-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"></path></svg></div>`;
}

/** The kit's ButtonGroup: one labelled row of buttons that read as a set. */
export function ButtonGroupBar(label: string, children: TemplateInstance): TemplateInstance {
  return html`<div class="ui-button-group" data-slot="button-group" data-orientation="horizontal" role="group" aria-label="${label}">${children}</div>`;
}

/** The kit's page heading: one h1 (with a status beside it), a muted description, and the page's actions at the end. */
export function PageHeader(options: {
  title: string;
  description: string;
  badge?: TemplateInstance;
  actions?: TemplateInstance;
  /** The heading rank: 1 for a page's own title, 2 for a section of a page that something else titles. */
  level?: 1 | 2;
}): TemplateInstance {
  const title =
    options.level === 2
      ? html`<h2 class="mx-page-header__title">${options.title}</h2>`
      : html`<h1 class="mx-page-header__title">${options.title}</h1>`;
  return html`
    <header class="mx-page-header">
      <div class="mx-page-header__text">
        <div class="mx-page-header__heading">
          ${title}
          ${options.badge === undefined ? html`` : options.badge}
        </div>
        <p class="mx-page-header__description">${options.description}</p>
      </div>
      ${options.actions === undefined ? html`` : html`<div class="mx-page-header__actions">${options.actions}</div>`}
    </header>
  `;
}

/** A kit Card with a header: title, optional description, optional link or control at the end. */
export function Section(options: {
  title: string;
  description?: string;
  action?: TemplateInstance;
  region?: string;
  children: TemplateInstance;
}): TemplateInstance {
  return html`
    <section class="ui-card mx-section" data-slot="card" data-region="${options.region ?? ''}">
      <div class="ui-card-header" data-slot="card-header">
        <h2 class="ui-card-title" data-slot="card-title">${options.title}</h2>
        ${options.description === undefined ? html`` : html`<p class="ui-card-description" data-slot="card-description">${options.description}</p>`}
        ${options.action === undefined ? html`` : html`<div class="ui-card-action" data-slot="card-action">${options.action}</div>`}
      </div>
      <div class="ui-card-content" data-slot="card-content">${options.children}</div>
    </section>
  `;
}

/** A link that reads as the kit's link button, for a card header's "all" affordance. */
export function CardLink(href: string, label: string): TemplateInstance {
  return LinkButton({ href, label, variant: 'link', size: 'sm' });
}

/** The kit's progress bar for a percentage: a track and its indicator, with the figure beside it. */
export function Progress(options: { value: number; label: string }): TemplateInstance {
  const { value } = options;
  return html`<div class="mx-progress">
    <div class="ui-progress" data-slot="progress" role="progressbar" aria-label="${options.label}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${String(value)}"><div class="ui-progress-indicator" data-slot="progress-indicator" style="transform: translateX(-${String(100 - value)}%)"></div></div>
    <small>${String(value)}%</small>
  </div>`;
}

export type Tone = 'good' | 'bad' | 'warn' | 'active' | 'neutral';

/** The kit Badge for a status word; tone picks the variant. */
export function KitBadge(label: string, tone: Tone): TemplateInstance {
  const variant = tone === 'good' ? 'default' : tone === 'bad' ? 'destructive' : tone === 'warn' ? 'outline' : 'secondary';
  return html`<span class="ui-badge mx-badge" data-slot="badge" data-variant="${variant}" data-tone="${tone}">${label}</span>`;
}

/** A kit Badge that labels rather than judges: a kind, a tag, a role. */
export function Chip(label: string): TemplateInstance {
  return html`<span class="ui-badge mx-badge" data-slot="badge" data-variant="outline">${label}</span>`;
}

/** A kit Avatar of initials; `lg` is the page's own identity, `sm` sits beside a name. */
export function Avatar(options: { initials: string; size?: 'sm' | 'default' | 'lg' }): TemplateInstance {
  return html`<span class="ui-avatar" data-slot="avatar" data-size="${options.size ?? 'default'}"><span class="ui-avatar-fallback" data-slot="avatar-fallback">${options.initials}</span></span>`;
}

/** Who: a kit Avatar of initials beside a name. */
export function Person(options: { initials: string; name: string; title?: string }): TemplateInstance {
  return html`<span class="mx-person" title="${options.title}"><span class="ui-avatar" data-slot="avatar" data-size="sm"><span class="ui-avatar-fallback" data-slot="avatar-fallback">${options.initials}</span></span>${options.name}</span>`;
}

/** A figure: a kit Card with what is counted, the number, and what the number covers. */
export function Metric(options: { label: string; value: string; note: string; tone?: Tone }): TemplateInstance {
  return html`
    <article class="ui-card mx-metric" data-slot="card" data-tone="${options.tone ?? 'neutral'}">
      <div class="ui-card-header" data-slot="card-header">
        <p class="ui-card-description" data-slot="card-description">${options.label}</p>
        <strong class="ui-card-title mx-metric__value" data-slot="card-title">${options.value}</strong>
      </div>
      <div class="ui-card-content" data-slot="card-content"><p class="mx-metric__note">${options.note}</p></div>
    </article>
  `;
}

/** A verdict stated beside the figures it rests on: a kit Alert, destructive when it is bad news. */
export function Callout(options: { label: string; note: string; tone: Tone }): TemplateInstance {
  return html`
    <div class="ui-alert mx-callout" data-slot="alert" data-variant="${options.tone === 'bad' ? 'destructive' : 'default'}" data-tone="${options.tone}" role="status">
      <div class="ui-alert-title" data-slot="alert-title">${options.label}</div>
      <div class="ui-alert-description" data-slot="alert-description">${options.note}</div>
    </div>
  `;
}

/**
 * The kit Empty for a region that holds nothing, or that the authority does
 * not project: what is absent and, in a sentence, why.
 */
export function EmptyBlock(options: { title: string; detail?: string; region?: string }): TemplateInstance {
  return html`
    <div class="ui-empty mx-empty" data-slot="empty" data-region="${options.region ?? ''}">
      <div class="ui-empty-header" data-slot="empty-header">
        <p class="ui-empty-title" data-slot="empty-title">${options.title}</p>
        ${options.detail === undefined ? html`` : html`<p class="ui-empty-description" data-slot="empty-description">${options.detail}</p>`}
      </div>
    </div>
  `;
}

export type LinkTab = { id: string; label: string; href: string; count?: number; title?: string };

/**
 * The kit's tabs list, as links, because each tab is an address. The lit tab
 * is the address on screen, so it carries `aria-current="page"`. Fresh objects
 * on every render so `repeat` re-diffs the lit tab on a client navigation
 * (#677). A count rides in the label: a tab's accessible name is its text.
 */
export function LinkTabs(options: { label: string; tabs: readonly LinkTab[]; active: string; region?: string }): TemplateInstance {
  return html`<nav class="ui-tabs-list mx-tabs" data-slot="tabs-list" data-region="${options.region ?? ''}" aria-label="${options.label}">
    ${repeat(
      options.tabs.map((tab) => ({ ...tab, on: tab.id === options.active })),
      (tab) => tab.id,
      (tab) => html`<a class="ui-tabs-trigger" data-slot="tabs-trigger" data-tab="${tab.id}" data-state="${tab.on ? 'active' : 'inactive'}" aria-current="${tab.on ? 'page' : undefined}" title="${tab.title}" href="${tab.href}" data-nav="${tab.href}">${tab.count === undefined ? tab.label : `${tab.label} ${String(tab.count)}`}</a>`,
    )}
  </nav>`;
}

export type KitColumn<Row> = { label: string; render: (row: Row) => TemplateInstance | string; align?: 'end' };

/** A kit Card that is one row of a list: a title, what it is, a status at the end, and facts below. */
export function RowCard(options: {
  title: TemplateInstance;
  description?: TemplateInstance | string;
  action?: TemplateInstance;
  facts?: ReadonlyArray<{ label: string; value: TemplateInstance | string }>;
  selected?: boolean;
}): TemplateInstance {
  const facts = options.facts ?? [];
  return html`<li class="ui-card mx-row-card" data-slot="card" data-state="${options.selected === true ? 'selected' : undefined}">
    <div class="ui-card-header" data-slot="card-header">
      <h3 class="ui-card-title mx-row-card__title" data-slot="card-title">${options.title}</h3>
      ${options.description === undefined ? html`` : html`<p class="ui-card-description" data-slot="card-description">${options.description}</p>`}
      ${options.action === undefined ? html`` : html`<div class="ui-card-action" data-slot="card-action">${options.action}</div>`}
    </div>
    ${facts.length === 0
      ? html``
      : html`<div class="ui-card-content" data-slot="card-content"><dl class="mx-facts">${repeat(facts, (fact) => fact.label, (fact) => html`<div><dt>${fact.label}</dt><dd>${fact.value}</dd></div>`)}</dl></div>`}
  </li>`;
}

/**
 * The kit Table: a scroll region a keyboard can reach, a header row, and the
 * kit's empty message. With `card`, a viewport narrower than 768px reads the
 * same rows as a list of kit Cards instead: a table's columns do not fit a
 * phone, and a table scrolled sideways hides the columns that matter.
 */
export function KitTable<Row>(options: {
  label: string;
  columns: ReadonlyArray<KitColumn<Row>>;
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  empty: string;
  card?: (row: Row) => TemplateInstance;
  /** The row the page is showing in detail; the kit marks it `data-state="selected"`. */
  selected?: (row: Row) => boolean;
}): TemplateInstance {
  if (options.rows.length === 0) {
    return html`<p class="mx-empty-note" role="status">${options.empty}</p>`;
  }
  const { card, selected } = options;
  const table = html`
    <div class="ui-table-container" data-slot="table-container" role="region" tabindex="0" aria-label="${options.label}">
      <table class="ui-table" data-slot="table">
        <thead class="ui-table-header" data-slot="table-header">
          <tr class="ui-table-row" data-slot="table-row">
            ${repeat(options.columns.map((column) => ({ column })), (item) => item.column.label, (item) => html`<th class="ui-table-head" data-slot="table-head" data-align="${item.column.align ?? 'start'}">${item.column.label}</th>`)}
          </tr>
        </thead>
        <tbody class="ui-table-body" data-slot="table-body">
          ${repeat(options.rows, options.rowKey, (row) => html`<tr class="ui-table-row" data-slot="table-row" data-state="${selected?.(row) === true ? 'selected' : undefined}">
            ${repeat(options.columns.map((column) => ({ column, row })), (item) => item.column.label, (item) => html`<td class="ui-table-cell" data-slot="table-cell" data-align="${item.column.align ?? 'start'}">${item.column.render(item.row)}</td>`)}
          </tr>`)}
        </tbody>
      </table>
    </div>
  `;
  if (card === undefined) return table;
  return html`<div class="mx-table-cards">
    <div class="mx-table-cards__table">${table}</div>
    <ul class="mx-table-cards__cards" aria-label="${options.label}">${repeat(options.rows, options.rowKey, (row) => card(row))}</ul>
  </div>`;
}

export type Crumb = { label: string; href: string | null };

/** The kit Breadcrumb: a link for each step but the last, which is the page and carries `aria-current`. */
export function Breadcrumb(options: { label: string; crumbs: readonly Crumb[]; className?: string }): TemplateInstance {
  const trail = options.crumbs.map((crumb, index) => ({ ...crumb, first: index === 0, key: `${String(index)}:${crumb.label}` }));
  return html`<nav class="ui-breadcrumb ${options.className ?? ''}" data-slot="breadcrumb" aria-label="${options.label}">
    <ol class="ui-breadcrumb-list" data-slot="breadcrumb-list">${repeat(trail, (crumb) => crumb.key, (crumb) => html`<li class="ui-breadcrumb-item" data-slot="breadcrumb-item">
      ${crumb.first
        ? html``
        : html`<span class="ui-breadcrumb-separator" data-slot="breadcrumb-separator" role="presentation" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="${GLYPH.chevronRight}"></path></svg></span>`}
      ${crumb.href === null
        ? html`<span class="ui-breadcrumb-page" data-slot="breadcrumb-page" aria-current="page">${crumb.label}</span>`
        : html`<a class="ui-breadcrumb-link" data-slot="breadcrumb-link" href="${crumb.href}" data-nav="${crumb.href}">${crumb.label}</a>`}
    </li>`)}</ol>
  </nav>`;
}

/** A kit Item that is a link: a title, what it leads to, and a chevron. */
export function LinkItem(options: { href: string; title: string; description: string }): TemplateInstance {
  return html`<a class="ui-item mx-link-item" data-slot="item" data-variant="outline" data-size="sm" href="${options.href}" data-nav="${options.href}">
    <div class="ui-item-content" data-slot="item-content">
      <div class="ui-item-title" data-slot="item-title">${options.title}</div>
      <p class="ui-item-description" data-slot="item-description">${options.description}</p>
    </div>
    <div class="ui-item-actions" data-slot="item-actions">${Icon(GLYPH.chevronRight)}</div>
  </a>`;
}

/**
 * The commands that change what a page only reads, in a kit Alert. The web
 * app never writes; the CLI does, and the page says which command.
 */
export function CliCommands(options: { label: string; detail: string; commands: readonly string[] }): TemplateInstance {
  return html`
    <div class="ui-alert mx-callout mx-cli" data-slot="alert" data-variant="default" data-tone="neutral" role="note">
      <div class="ui-alert-title" data-slot="alert-title">${options.label}</div>
      <div class="ui-alert-description" data-slot="alert-description">
        <p>${options.detail}</p>
        <pre class="mx-state__command"><code>${options.commands.join('\n')}</code></pre>
      </div>
    </div>
  `;
}
